// Werkstoff-Archiv (Info) für die Einkauf-Sidebar: alle Werkstoffe der echten
// Daten, gruppiert nach EN 10027-2 (Nummernsystem) mit Kurzname, aufklappbar.
// Klick auf einen Werkstoff filtert die Blechbedarf-Liste danach.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useApi } from '../hooks/useApi';

const WG_ORDER = [
  'Baustahl (unlegiert)',
  'Höherfester / legierter Stahl',
  'Edelstahl (rostfrei)',
  'Hitzebeständiger Stahl',
  'Aluminium',
  'Kupfer / NE-Metall',
  'Beschichtet / Sonstiges',
] as const;

function gruppe(w: string): (typeof WG_ORDER)[number] {
  const t = w.trim().toLowerCase();
  const m = t.match(/^(\d)\.(\d)(\d)/);
  if (m) {
    if (m[1] === '3') return 'Aluminium';
    if (m[1] === '2') return 'Kupfer / NE-Metall';
    if (m[1] === '1') {
      const gr = parseInt(m[2]! + m[3]!, 10);
      if (gr <= 9) return 'Baustahl (unlegiert)';
      if (gr >= 40 && gr <= 45) return 'Edelstahl (rostfrei)';
      if (gr >= 46 && gr <= 49) return 'Hitzebeständiger Stahl';
      return 'Höherfester / legierter Stahl';
    }
  }
  if (/verzinkt|foliert|beschichtet|lochblech|hardox/.test(t)) return 'Beschichtet / Sonstiges';
  if (/edelstahl|rostfrei|v2a|v4a|^x\d/.test(t)) return 'Edelstahl (rostfrei)';
  if (/s\d{3}.*(mc|ml)|feinkorn/.test(t)) return 'Höherfester / legierter Stahl';
  if (/^(s\d{3}|p\d{3}|e\d{3}|dc|dx|st\b|c\d{2}|baustahl)/.test(t)) return 'Baustahl (unlegiert)';
  if (/almg|alsi|^alu|^al\s?\d/.test(t)) return 'Aluminium';
  if (/kupfer|^cu\b|messing|bronze/.test(t)) return 'Kupfer / NE-Metall';
  return 'Beschichtet / Sonstiges';
}
function sortKey(w: string): string {
  const m = w.trim().match(/^(\d)\.(\d{4})/);
  return m ? `A${m[1]}.${m[2]}` : `Z${w.trim().toLowerCase()}`;
}

export function WerkstoffArchiv() {
  const [offen, setOffen] = useState(false);
  const navigate = useNavigate();
  const opt = useApi(() => api.einkauf.optionen(), []);
  const werkstoffe = opt.data?.werkstoffe ?? [];
  const kurz = opt.data?.werkstoffKurz ?? {};

  const label = (w: string): string => {
    const t = w.trim();
    if (kurz[t]) return `${t} · ${kurz[t]}`;
    const nr = t.match(/^(\d\.\d{4})/);
    if (nr && kurz[nr[1]!]) return `${t} · ${kurz[nr[1]!]}`;
    return t;
  };

  const map = new Map<string, string[]>();
  for (const w of werkstoffe) map.set(gruppe(w), [...(map.get(gruppe(w)) ?? []), w]);
  for (const arr of map.values()) arr.sort((a, b) => sortKey(a).localeCompare(sortKey(b), 'de', { numeric: true }));
  const gruppen = WG_ORDER.filter(g => map.has(g)).map(g => [g, map.get(g)!] as const);

  const waehle = (w: string) => { setOffen(false); navigate(`/blechbedarf?status=alle&werkstoff=${encodeURIComponent(w)}`); };

  return (
    <div className="mt-4 border-t border-slate-700 pt-3">
      <button type="button" onClick={() => setOffen(o => !o)}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-slate-300 hover:bg-slate-800 text-left">
        <span>📋 Werkstoff-Archiv</span>
        <span className="ml-auto text-xs text-slate-500">{werkstoffe.length}</span>
        <span className="text-slate-500">{offen ? '▾' : '▸'}</span>
      </button>
      {offen && (
        <div className="mt-1 max-h-[45vh] overflow-y-auto pr-1">
          {werkstoffe.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">Lädt …</div>}
          {gruppen.map(([g, werte]) => (
            <div key={g} className="mb-2">
              <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{g}</div>
              {werte.map(w => (
                <button key={w} type="button" onClick={() => waehle(w)}
                        className="block w-full text-left px-3 py-1 text-xs text-slate-300 hover:bg-slate-800 hover:text-white rounded">
                  {label(w)}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
